import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation } from "@apollo/client";
import { History, MoreHorizontal, Pencil, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { DeleteConversationDocument } from "@/graphql/deleteConversation.api";
import { RenameConversationDocument } from "@/graphql/renameConversation.api";

interface ConversationHistoryProps {
  conversations: Array<{ id: string; title?: string | null; createdAt: string }>;
  activeId: string | null;
  onSelect: (id: string) => void;
  onNewChat: () => void;
  onDelete?: (id: string) => void;
  onRename?: (id: string, newTitle: string) => void;
}

export function ConversationHistory({
  conversations,
  activeId,
  onSelect,
  onNewChat,
  onDelete,
  onRename,
}: ConversationHistoryProps) {
  const { t } = useTranslation("common");
  const [deleteConversation] = useMutation(DeleteConversationDocument);
  const [renameConversation] = useMutation(RenameConversationDocument);

  const [renameDialogOpen, setRenameDialogOpen] = useState(false);
  const [conversationToRename, setConversationToRename] = useState<{
    id: string;
    title: string;
  } | null>(null);
  const [newTitle, setNewTitle] = useState("");

  const handleDelete = async (id: string) => {
    try {
      const { data } = await deleteConversation({ variables: { id } });
      if (data?.deleteConversation?.success) {
        onDelete?.(id);
      }
    } catch (error) {
      console.error("Failed to delete conversation:", error);
    }
  };

  const openRenameDialog = (conv: { id: string; title?: string | null }) => {
    setConversationToRename({ id: conv.id, title: conv.title || "" });
    setNewTitle(conv.title || "");
    setRenameDialogOpen(true);
  };

  const handleRename = async () => {
    if (!conversationToRename || !newTitle.trim()) return;

    try {
      const { data } = await renameConversation({
        variables: { id: conversationToRename.id, title: newTitle.trim() },
      });
      if (data?.renameConversation?.conversation) {
        onRename?.(conversationToRename.id, newTitle.trim());
        setRenameDialogOpen(false);
        setConversationToRename(null);
        setNewTitle("");
      }
    } catch (error) {
      console.error("Failed to rename conversation:", error);
    }
  };

  if (conversations.length === 0) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center p-6 text-center">
        <History className="h-10 w-10 text-neutral-300 mb-3" />
        <p className="text-sm text-neutral-500">{t("chat.noHistory")}</p>
        <Button variant="outline" size="sm" className="mt-3" onClick={onNewChat}>
          {t("chat.startNew")}
        </Button>
      </div>
    );
  }

  return (
    <>
      <div className="flex-1 overflow-y-auto">
        {conversations.map((conv) => (
          <div
            key={conv.id}
            onClick={() => onSelect(conv.id)}
            className={cn(
              "group w-full px-4 py-3 text-left border-b border-neutral-100 hover:bg-neutral-50 transition-colors cursor-pointer flex items-start justify-between gap-2",
              activeId === conv.id && "bg-neutral-100"
            )}
          >
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-neutral-800 truncate">
                {conv.title || t("chat.untitled")}
              </p>
              <p className="text-xs text-neutral-500 mt-0.5">
                {new Date(conv.createdAt).toLocaleDateString()}
              </p>
            </div>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  onClick={(e) => e.stopPropagation()}
                  className="opacity-0 group-hover:opacity-100 p-1 hover:bg-neutral-200 rounded transition-opacity"
                >
                  <MoreHorizontal className="h-4 w-4 text-neutral-500" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
                <DropdownMenuItem onClick={() => openRenameDialog(conv)}>
                  <Pencil className="h-4 w-4 mr-2" />
                  {t("chat.rename")}
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => handleDelete(conv.id)}
                  className="text-red-600 focus:text-red-600"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  {t("chat.delete")}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        ))}
      </div>

      <Dialog open={renameDialogOpen} onOpenChange={setRenameDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("chat.renameChat")}</DialogTitle>
            <DialogDescription>{t("chat.renameChatDescription")}</DialogDescription>
          </DialogHeader>
          <Input
            value={newTitle}
            onChange={(e) => setNewTitle(e.target.value)}
            placeholder={t("chat.chatNamePlaceholder")}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                handleRename();
              }
            }}
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setRenameDialogOpen(false)}>
              {t("common.cancel")}
            </Button>
            <Button onClick={handleRename} disabled={!newTitle.trim()}>
              {t("common.save")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
